import {
    STORE_LOADER_DISMISSED_ATTRIBUTE,
    STORE_LOADING_COVER_WRAP_SELECTOR,
} from './hideStoreLoadingCover';

/**
 * Run `task` once the store loading cover has been dismissed, plus
 * `delayAfterDismissMs` of settling time — or after `capMs` from now,
 * whichever comes first. The cap guarantees the task can be delayed by a
 * broken cover flow but never dropped.
 *
 * Used to keep deferrable boot work (telemetry SDK downloads and their eval
 * long-tasks) off the shop's critical path: while the cover is up the
 * shared-ui template chunk is still downloading, and every extra request
 * competes with it for bandwidth while every extra eval competes with it for
 * the main thread.
 *
 * When there is no cover on the page and no dismissed flag (e.g. a client
 * navigation onto /shop, where the SSR cover was never emitted), the task is
 * scheduled immediately with `delayAfterDismissMs`.
 *
 * Returns a cancel function; the task runs at most once.
 */
export function runAfterCoverDismissed(
    task: () => void,
    options: { delayAfterDismissMs: number; capMs: number },
): () => void {
    if (typeof window === 'undefined') {
        return () => undefined;
    }

    let finished = false;
    let observer: MutationObserver | undefined;
    let delayTimer: ReturnType<typeof setTimeout> | undefined;
    let capTimer: ReturnType<typeof setTimeout> | undefined;

    const cancel = () => {
        finished = true;
        observer?.disconnect();
        observer = undefined;
        if (delayTimer !== undefined) {
            clearTimeout(delayTimer);
            delayTimer = undefined;
        }
        if (capTimer !== undefined) {
            clearTimeout(capTimer);
            capTimer = undefined;
        }
    };

    const run = () => {
        if (finished) {
            return;
        }
        cancel();
        task();
    };

    const scheduleRun = () => {
        if (finished || delayTimer !== undefined) {
            return;
        }
        observer?.disconnect();
        observer = undefined;
        delayTimer = setTimeout(run, options.delayAfterDismissMs);
    };

    const isDismissed = () =>
        document.documentElement.hasAttribute(STORE_LOADER_DISMISSED_ATTRIBUTE);
    const hasCover = () =>
        document.querySelector(STORE_LOADING_COVER_WRAP_SELECTOR) !== null;

    if (isDismissed() || !hasCover()) {
        scheduleRun();
    } else {
        observer = new MutationObserver(() => {
            if (isDismissed()) {
                scheduleRun();
            }
        });
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: [STORE_LOADER_DISMISSED_ATTRIBUTE],
        });
        capTimer = setTimeout(run, options.capMs);
    }

    return cancel;
}
